import { useEffect, useState } from 'react';
import { Form, Radio, RadioChangeEvent } from 'antd';
import FormItem from 'antd/lib/form/FormItem';
import { PlusOutlined } from '@ant-design/icons';
import { NavLink, useNavigate, useParams } from 'react-router-dom';

import Button from '../../../components/Button/Button';
import DatePicker from '../../../components/DatePicker/DatePicker';
import { Book, Book as BookModel } from '../../../models/books';
import { createErrorNotification, createSuccessNotification } from '../../../service/notification';
import { getFormattedClubName, navigateToClub } from '../../../utils/club';
import { useUserStore } from '../../../store/useUser';
import { createElection } from '../../../service/election';
import useHeader from '../../../hooks/useHeader';
import BookElectionList from './BookElectionList';
import RemoveSelectedBook from './RemoveSelectedBook';
import MeetingCandidate from './MeetingCandidate';
import BookElectionSearch from './BookElectionSearch';
import SelectedBook from './SelectedBook';

import styles from './AddElection.module.scss';
import BookCandidate from './BookCandidate';

const AddElection = () => {
  const { id, name: clubName } = useParams();

  const [form] = Form.useForm();
  const navigate = useNavigate();

  const userId = useUserStore(state => state.id)

  const [isLoading, setIsLoading] = useState(false);
  const [typeVote, setTypeVote] = useState('meet');
  const [selectedBooks, setSelectedBooks] = useState<{ [key: number]: BookModel }>([]);
  const [booksSearchResults, setBooksSearchResults] = useState<{[key: number]: BookModel[]}>({})

  useHeader({ header: getFormattedClubName(), subheader: 'Cadastro de Votação' });

  const clubId = Number(id);

  const onFinish = async (values) => {
    if (isLoading) return

    setIsLoading(true)

    const format = 'YYYY-MM-DD HH:mm'

    values.finalDate = values.finalDate.format(format)
    values.initialDate = values.initialDate.format(format)

    if (values.candidates.length > 0 && typeVote === 'meet') {
      values.candidates = values.candidates.map(dates => {
        const st = dates.format(format)
        return st
      })
    }

    const { error, success } = await createElection(clubId, userId, values);

    setIsLoading(false)

    if (error) return createErrorNotification(error)

    createSuccessNotification(success)

    navigate(navigateToClub(clubName, clubId, '/vote'), { replace: true })
  }

  const addItemToSelectedBooks = (selectedBook: Book, index) => {
    const formValues = form.getFieldsValue()

    selectedBooks[index] = selectedBook;

    setSelectedBooks(selectedBooks)

    formValues["candidates"][index] = selectedBook

    form.setFieldsValue({ candidates: formValues.candidates })

    return selectedBook.name
  }

  return(
    <div className={styles.container}>
      <div className={styles.maskContainer}></div>
        <div className={styles.createContainer}>
          <div className={styles.createForm}>
            <Form 
              layout="vertical"
              form={form}
              onFinish={onFinish}
              id="formLogin" 
              className={styles.FormContainer}
            >
              <h2 className={styles.title}>Criar nova eleição</h2>
              <FormItem
                label="Tipo da Votação"
                name="typeVote"
                initialValue={typeVote}
                required
              >
                <Radio.Group 
                  value={typeVote}
                  defaultValue={typeVote}
                  onChange={(e)=>{
                    setTypeVote(e.target.value);
                    form.setFieldsValue({typeVote:e.target.value})
                  }} >
                    <Radio value='meet'>Encontro</Radio>
                    <Radio value='book'>Livro</Radio>
                </Radio.Group>
              </FormItem>
              <FormItem
                label="Inicio"
                name="initialDate"
                required
                rules={[{required: true, message: 'Por favor, insira a data e a hora!'}]}
                >
                  <DatePicker
                    showTime={{ format: 'HH:mm' }}
                    format="DD/MM/YYYY HH:mm"
                    onChange={(value, string) => {
                      return string
                    }}
                    />
              </FormItem>
              <FormItem
                label="Término"
                name="finalDate"
                required
                rules={[{required: true, message: 'Por favor, insira a data e a hora!'}]}
                >
                  <DatePicker
                    showTime={{ format: 'HH:mm' }}
                    format="DD/MM/YYYY HH:mm"
                    onChange={(value, string) => {
                      return string
                    }}
                    />
              </FormItem>
              <Form.List
                name="candidates"
                rules={[
                  {
                    validator: async (_, names) => {
                      if (!names || names.length < 2) {
                        return Promise.reject(new Error('Ao menos 1 candidatos'));
                      }
                    },
                  },
                ]}
              >
                {(fields, { add, remove }, { errors }) => (
                  <>
                    {fields.map((field, index) => {
                      const selectedBook = Object.values(selectedBooks)[index] || null

                      return (
                        <>
                        {typeVote === 'meet' && <MeetingCandidate index={index} field={field}/> }
                        {typeVote === 'book' && (
                          <BookCandidate
                            addItemToSelectedBooks={addItemToSelectedBooks}
                            booksSearchResults={booksSearchResults}
                            index={index}
                            selectedBook={selectedBook}
                            selectedBooks={selectedBooks}
                            setBooksSearchResults={setBooksSearchResults}
                          />
                        )}
                      </>
                    )})}
                    <Form.Item>
                      <Button
                        type="dashed"
                        onClick={() => add()}
                        style={styles.button}
                      >
                        Adicionar candidato
                      </Button>
                      <Form.ErrorList errors={errors} />
                    </Form.Item>
                  </>
                )}
              </Form.List>
            <Button submit>Criar Votação</Button>
          </Form>
        </div>
      </div>
    </div>
  )
}

export default AddElection;